﻿import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  XCircle,
  RotateCcw,
  Clock,
  FileText,
  Trash2,
  History,
  Download,
} from 'lucide-react';
import { PERMISSIONS, type ApiError, type EmployeePenalty } from '@pms/shared';
import { monthDateRange } from '@/lib/dateFormat';
import { penaltyService } from '@/services/penalty.service';
import { deleteAccessService } from '@/services/deleteAccess.service';
import { usePermission } from '@/hooks/usePermission';
import { BackfillPenaltiesModal } from './BackfillPenaltiesModal';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function formatHours(minutes: number): string {
  if (!minutes || minutes <= 0) return '0h 0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatPunchTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

type StatusFilter = 'pending' | 'approved' | 'rejected';

const TAB_KEYS: StatusFilter[] = ['pending', 'approved', 'rejected'];
const TAB_LABELS: Record<StatusFilter, string> = {
  pending:  'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_BADGE: Record<StatusFilter, string> = {
  pending:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

// ── Reject Dialog ──────────────────────────────────────────────────────────────

function RejectDialog({
  open,
  onClose,
  onConfirm,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState('');

  function handleClose() {
    setReason('');
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-sm bg-popover backdrop-blur-xl">
        <DialogHeader>
          <DialogTitle className="text-rose-600 dark:text-rose-400">Reject Penalty</DialogTitle>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Rejection reason…"
          rows={3}
          className="text-sm resize-none"
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            disabled={!reason.trim() || loading}
            onClick={() => onConfirm(reason)}
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            {loading ? 'Rejecting…' : 'Confirm Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function HrmsPenaltyPage() {
  const queryClient = useQueryClient();
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.PENALTY_MANAGE);
  const canView   = has(PERMISSIONS.PENALTY_VIEW_ALL);

  const { data: penaltyDeletePerm } = useQuery({
    queryKey: ['delete-access', 'my-permission', 'penalty'],
    queryFn: () => deleteAccessService.getMyPermission('penalty'),
  });
  const canDeletePenalty = penaltyDeletePerm?.can_delete ?? false;

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filterMonth, setFilterMonth] = useState<number | ''>('');
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; label: string } | null>(null);
  const [backfillOpen, setBackfillOpen] = useState(false);

  const monthRange = filterMonth !== '' ? monthDateRange(filterYear, filterMonth) : undefined;
  const dateFrom = monthRange?.from;
  const dateTo   = monthRange?.to;

  // Export — same tab and filters as the list, every page; the server reads live data on each click
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await penaltyService.exportXlsx({
        status: statusFilter,
        q: search || undefined,
        date_from: dateFrom,
        date_to: dateTo,
      });
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => penaltyService.delete(id),
    onSuccess: () => {
      toast.success('Penalty deleted');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['penalty'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'salary'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'salary', 'me'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['penalty', { status: statusFilter, q: search || undefined, page, dateFrom, dateTo }],
    queryFn: () =>
      penaltyService.list({ status: statusFilter, q: search || undefined, page, limit: 20, date_from: dateFrom, date_to: dateTo }),
    enabled: canView,
  });

  // Only to explain the waiting period in the header — the rule itself is enforced
  // entirely on the backend; nothing here recomputes it.
  const { data: penaltyConfig } = useQuery({
    queryKey: ['penalty', 'config'],
    queryFn: () => penaltyService.getConfig(),
    enabled: canView,
  });
  const gapDays = penaltyConfig?.penalty_gap_days ?? null;

  const penalties = data?.data ?? [];
  const pagination = data?.pagination;

  const handleTabChange = (s: StatusFilter) => {
    setStatusFilter(s);
    setPage(1);
    setFilterMonth('');
  };

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Penalty Approvals</h1>
        <p className="text-sm text-muted-foreground">
          Review and approve attendance-based penalty deductions for salary computation.
        </p>
        {gapDays !== null && gapDays > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            Penalties are held for {gapDays} {gapDays === 1 ? 'day' : 'days'} after the attendance
            date, so a short leave or half-day leave has time to be approved. Recent lateness will
            not appear here yet, and lateness covered by approved leave never will.
          </p>
        )}
      </div>

      <Tabs
        tabs={TAB_KEYS.map((k) => ({ key: k, label: TAB_LABELS[k] }))}
        active={statusFilter}
        onChange={(k) => handleTabChange(k as StatusFilter)}
      />

      {/* Main card */}
      <div className="rounded-lg border border-border/60 bg-card">

        {/* Card header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/40 px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-semibold">
              {TAB_LABELS[statusFilter]} Penalties
            </span>
            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground">
              {pagination?.total ?? 0}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder="Search name/code/reason…"
              containerClassName="w-52"
            />
            <Select
              value={filterMonth === '' ? 'all' : String(filterMonth)}
              onValueChange={(v) => { setFilterMonth(v === 'all' ? '' : Number(v)); setPage(1); }}
            >
              <SelectTrigger className="h-9 w-36 text-sm">
                <SelectValue placeholder="All Months" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(filterYear)}
              onValueChange={(v) => { setFilterYear(Number(v)); setPage(1); }}
            >
              <SelectTrigger className="h-9 w-24 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[new Date().getFullYear() - 2, new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterMonth !== '' && (
              <Button variant="outline" size="sm" className="h-9 px-2.5" onClick={() => { setFilterMonth(''); setPage(1); }}>
                ✕
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canView && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => void handleExport()}
                disabled={exporting}
                title="Every penalty on this tab matching the current filters, with a per-employee summary"
              >
                <Download className="h-3.5 w-3.5" />
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            )}
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBackfillOpen(true)}
              >
                <History className="h-3.5 w-3.5" />
                Backfill
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        {!canView ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            You don't have permission to view penalties.
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-36 animate-pulse rounded-xl bg-muted/40" />
            ))}
          </div>
        ) : isError ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">Failed to load penalties.</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : penalties.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No {statusFilter} penalties
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
            {penalties.map((p) => (
              <PenaltyCard
                key={p.id}
                penalty={p}
                canManage={canManage}
                onDelete={canDeletePenalty ? (id, label) => setDeleteConfirm({ id, label }) : undefined}
                onInvalidate={() => {
                queryClient.invalidateQueries({ queryKey: ['penalty'] });
                queryClient.invalidateQueries({ queryKey: ['payroll', 'salary'] });
                queryClient.invalidateQueries({ queryKey: ['payroll', 'salary', 'me'] });
              }}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border/40 px-4 py-3 text-sm">
            <span className="text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
            </span>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ‹
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                ›
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <Dialog open onOpenChange={(v) => !v && setDeleteConfirm(null)}>
          <DialogContent className="max-w-sm bg-popover backdrop-blur-xl">
            <DialogHeader>
              <DialogTitle className="text-destructive">Delete Penalty</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Permanently delete this penalty for{' '}
              <span className="font-medium text-foreground">{deleteConfirm.label}</span>?
              This cannot be undone.
            </p>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleteMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {backfillOpen && <BackfillPenaltiesModal onClose={() => setBackfillOpen(false)} />}
    </div>
  );
}

// ── Penalty Card ───────────────────────────────────────────────────────────────

function PenaltyCard({
  penalty,
  canManage,
  onDelete,
  onInvalidate,
}: {
  penalty: EmployeePenalty;
  canManage: boolean;
  onDelete?: (id: string, label: string) => void;
  onInvalidate: () => void;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);

  const approve = useMutation({
    mutationFn: () => penaltyService.approve(penalty.id),
    onSuccess: () => { toast.success('Penalty approved'); onInvalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reject = useMutation({
    mutationFn: (reason: string) => penaltyService.reject(penalty.id, reason),
    onSuccess: () => {
      toast.success('Penalty rejected');
      setRejectOpen(false);
      onInvalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const moveToPending = useMutation({
    mutationFn: () => penaltyService.moveToPending(penalty.id),
    onSuccess: () => { toast.success('Moved to pending'); onInvalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const initials = [penalty.first_name?.[0], penalty.last_name?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase() || '?';

  const statusBadge = STATUS_BADGE[penalty.status as keyof typeof STATUS_BADGE] ?? '';

  return (
    <>
      <div className="rounded-xl bg-card border border-border/60 shadow-sm p-3.5 space-y-2.5">

        {/* Row 1: avatar + name + badges */}
        <div className="flex items-center gap-2.5">
          {penalty.profile_picture_url ? (
            <img
              src={penalty.profile_picture_url}
              alt=""
              className="h-9 w-9 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 text-xs font-semibold">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm text-foreground truncate">
                {penalty.first_name} {penalty.last_name}
              </span>
              <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                {/* Occurrence badge — only for late_arrival with a tracked occurrence */}
                {penalty.penalty_type === 'late_arrival' && penalty.occurrence_number != null && (
                  <span className="inline-flex rounded-full bg-violet-100 text-violet-700 px-1.5 py-0.5 text-[10px] font-medium">
                    {ordinal(penalty.occurrence_number)} Late
                  </span>
                )}
                <span className="inline-flex rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {penalty.source === 'auto' ? 'Auto' : 'Manual'}
                </span>
                <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize ${statusBadge}`}>
                  {penalty.status}
                </span>
                {canManage && onDelete && (
                  <button
                    type="button"
                    className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    onClick={() => onDelete(penalty.id, `${penalty.first_name ?? ''} ${penalty.last_name ?? ''}`.trim())}
                    title="Delete penalty"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            {penalty.employee_code && (
              <span className="text-xs text-muted-foreground">{penalty.employee_code}</span>
            )}
          </div>
        </div>

        {/* Row 2: date + hours + created */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1 font-medium text-foreground/80">
            <Clock className="h-3.5 w-3.5" />
            {new Date(penalty.attendance_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
          {penalty.first_punch_in && (
            <span className="flex items-center gap-1">
              Punched in at {formatPunchTime(penalty.first_punch_in)}
            </span>
          )}
          {penalty.penalty_type === 'late_arrival' && penalty.late_by_minutes != null && penalty.late_by_minutes > 0 && (
            <span className="flex items-center gap-1 text-rose-600 font-medium">
              Late by {penalty.late_by_minutes} min
            </span>
          )}
          <span className="flex items-center gap-1 text-rose-600">
            <AlertTriangle className="h-3 w-3" />
            {penalty.penalty_minutes === 0
              ? 'Warning (0 min)'
              : formatHours(penalty.penalty_minutes)}
          </span>
        </div>

        {/* Row 3: reason */}
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground/60" />
          <span className="break-words min-w-0">{penalty.reason}</span>
        </div>

        {/* Row 4: rejection remark */}
        {penalty.status === 'rejected' && penalty.rejection_reason && (
          <p className="text-[10px] text-red-500/80 pl-5 italic line-clamp-2 break-all overflow-hidden">
            "{penalty.rejection_reason}"
          </p>
        )}

        {/* Action buttons */}
        {canManage && (
          <>
            {penalty.status === 'pending' && (
              <div className="flex gap-1.5 pt-0.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => setRejectOpen(true)}
                  disabled={approve.isPending}
                >
                  <XCircle className="h-3.5 w-3.5 mr-1" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  className="flex-1 h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate()}
                >
                  <CheckCircle className="h-3.5 w-3.5 mr-1" />
                  {approve.isPending ? 'Approving…' : 'Approve'}
                </Button>
              </div>
            )}
            {(penalty.status === 'approved' || penalty.status === 'rejected') && (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs"
                disabled={moveToPending.isPending}
                onClick={() => moveToPending.mutate()}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                {moveToPending.isPending ? 'Moving…' : 'Move to Pending'}
              </Button>
            )}
          </>
        )}
      </div>

      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={(reason) => reject.mutate(reason)}
        loading={reject.isPending}
      />

    </>
  );
}
